import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from './ui/alert-dialog';
import { useWorkflow } from '@/context/workflow-context-simple';
import { useWorkflowApi } from '@/hooks/use-workflow-api';
import { useToast } from '@/hooks/use-toast';
import { getFacetKeysFromConfiguration } from '@/utils/column-type-helpers';

interface MoveToModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedItems: string[];
  configuration: any;
  currentStage: string;
  refreshData: (customFilterArray?: string[], orderBy?: string, facetKeys?: string[]) => void;
  setSelectedItems: (items: string[]) => void;
  language: string;
  t: (key: string) => string;
  currentOrderBy?: string;
}

export const MoveToModal: React.FC<MoveToModalProps> = ({ isOpen, onClose, selectedItems, configuration, currentStage, refreshData, setSelectedItems, language, t, currentOrderBy }) => {
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { stageFilters, globalFilters, removeWorkOrdersBulk } = useWorkflow();
  const { bulkUpdateReports } = useWorkflowApi();
  const { toast } = useToast();

  // Get Actions field from the current stage's fieldConfigurations
  const stageConfig = configuration?.stageConfigurations?.find((s: any) => s.stageCode.toLowerCase() === currentStage?.toLowerCase());
  const actionsField = stageConfig?.fieldConfigurations?.find((f: any) => f.columnCode === 'Actions');
  const options = Array.isArray(actionsField?.customValues)
    ? actionsField.customValues.filter((v: any) => !v.isDeleted)
    : [];

  const handleMove = () => {
    if (!selectedStatus) return;
    setIsConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      // Build updates array for bulk update (same format as bulk edit modal)
      const updates = selectedItems.map(id => ({
        id,
        updates: { stage: selectedStatus } // Use camelCase field name
      }));

      // Call the bulk update API first (don't update UI until we know it succeeded)
      const result = await bulkUpdateReports(updates);
      
      // Handle the new response structure
      const { successUpdateCount, failedUpdateCount, messages } = result;
      
      // Build filter array once (used for refresh) - moved before conditional blocks
      const stageConfig = configuration?.stageConfigurations?.find((s: any) => s.stageCode.toLowerCase() === currentStage?.toLowerCase());
      const currentStageFilters = stageFilters.filter(f => f.stageId === stageConfig?.stageId);
      const allFilters = [...globalFilters, ...currentStageFilters].map(f => f.filter).filter(Boolean);
      const baseFilter = `Stage eq '${currentStage}'`;
      const filterArray = allFilters.length > 0 
        ? [`${baseFilter} and ${allFilters.join(' and ')}`]
        : [baseFilter];
      
      const facetKeys = getFacetKeysFromConfiguration(configuration);
      
      // Prepare toast data but DO NOT show yet; wait until refresh stabilizes
      let toastVariant: 'success' | 'default' | 'destructive' | undefined;
      let toastTitle: string | undefined;
      let toastDescription: string | undefined;
      if (failedUpdateCount === 0) {
        toastVariant = 'success';
        toastTitle = t('success') || 'Success';
        toastDescription = t('moveToSuccess')?.replace('{count}', String(successUpdateCount)) || 
                           `Successfully moved ${successUpdateCount} record${successUpdateCount !== 1 ? 's' : ''}.`;
      } else if (successUpdateCount === 0) {
        toastVariant = 'destructive';
        const errorDetails = messages && messages.length > 0 
          ? `:\n${messages.map((msg: string, index: number) => `${index + 1}. ${msg}`).join('\n')}` 
          : '';
        toastTitle = t('error') || 'Error';
        toastDescription = t('moveToFailed')?.replace('{count}', String(failedUpdateCount)).replace('{details}', errorDetails) || 
                           `Failed to move ${failedUpdateCount} record${failedUpdateCount !== 1 ? 's' : ''}${errorDetails}`;
      } else {
        toastVariant = 'default';
        const errorDetails = messages && messages.length > 0 
          ? `:\n${messages.map((msg: string, index: number) => `${index + 1}. ${msg}`).join('\n')}` 
          : '';
        toastTitle = t('partialSuccess') || 'Partial Success';
        toastDescription = t('moveToPartial')?.replace('{success}', String(successUpdateCount)).replace('{failed}', String(failedUpdateCount)).replace('{details}', errorDetails) || 
                           `Successfully moved ${successUpdateCount} record${successUpdateCount !== 1 ? 's' : ''}. ${failedUpdateCount} record${failedUpdateCount !== 1 ? 's' : ''} failed${errorDetails}`;
      }

      // Only continue if there were any successful updates
      if (successUpdateCount > 0) {
        setIsConfirmOpen(false);
        setSelectedStatus(null);
        // Do not clear selection or close the modal yet; wait until data is consistent
        
        // Optimistically remove successfully moved items from local state immediately
        const succeededIdsFromApi = Array.isArray((result as any)?.succeededIds)
          ? (result as any).succeededIds as string[]
          : null;
        const idsToRemove = succeededIdsFromApi && succeededIdsFromApi.length > 0
          ? succeededIdsFromApi
          : selectedItems;
        removeWorkOrdersBulk(idsToRemove);
        
        // Refresh data AFTER successful API call - add a short backoff to allow index to catch up
        const backoffs = [300, 800, 1500];
        for (let i = 0; i < backoffs.length; i++) {
          await new Promise(resolve => setTimeout(resolve, backoffs[i]));
          await refreshData(filterArray, currentOrderBy, facetKeys);
        }
        // Final refresh once more
        await refreshData(filterArray, currentOrderBy, facetKeys);
        
        // Now that we've refreshed (with backoff), show the toast and close the modal
        if (toastVariant && toastTitle && toastDescription) {
          toast({ variant: toastVariant, title: toastTitle, description: toastDescription });
        }
        setSelectedItems([]);
        onClose();
      } else {
        // Keep modal open for all failed updates so user can retry, but still refresh data
        setIsConfirmOpen(false);
        
        // Refresh data even on failures to show correct state
        await refreshData(filterArray, currentOrderBy, facetKeys);
        
        // Show failure/partial toast after refresh as well for consistent UX
        if (toastVariant && toastTitle && toastDescription) {
          toast({ variant: toastVariant, title: toastTitle, description: toastDescription });
        }
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t('error') || 'Error',
        description: error?.message || t('moveToError') || 'An error occurred while moving records.',
      });
      
      // On error, refresh data to restore the correct state
      const stageConfig = configuration?.stageConfigurations?.find((s: any) => s.stageCode.toLowerCase() === currentStage?.toLowerCase());
      const currentStageFilters = stageFilters.filter(f => f.stageId === stageConfig?.stageId);
      const allFilters = [...globalFilters, ...currentStageFilters].map(f => f.filter).filter(Boolean);
      const baseFilter = `Stage eq '${currentStage}'`;
      const filterArray = allFilters.length > 0 
        ? [`${baseFilter} and ${allFilters.join(' and ')}`]
        : [baseFilter];
      
      const facetKeys = getFacetKeysFromConfiguration(configuration);
      
      // Refresh data after error to show correct state
      await refreshData(filterArray, currentOrderBy, facetKeys);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={open => { if (!open) onClose(); }}>
        <DialogContent className="w-[95vw] sm:w-[90vw] max-w-[425px] min-w-[300px] sm:min-w-[350px]">
          <DialogHeader>
            <DialogTitle>{t('moveTo') || 'Move to'}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="grid gap-2">
              {options.map((option: any) => (
                <div key={option.code} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id={option.code}
                    name="moveTo"
                    value={option.code}
                    checked={selectedStatus === option.code}
                    onChange={() => setSelectedStatus(option.code)}
                    className="mr-2"
                  />
                  <label htmlFor={option.code} className="text-sm">
                    {language === 'fr' ? option.titleFrench : option.titleEnglish || option.code}
                  </label>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="elliptical-outline" onClick={onClose} disabled={isSubmitting}>{t('cancel') || 'Cancel'}</Button>
            <Button variant="elliptical" onClick={handleMove} disabled={!selectedStatus || isSubmitting}>{t('ok') || 'Ok'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('areYouSure') || 'Are you sure?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('moveToConfirm')
                ? t('moveToConfirm')
                    .replace('{count}', String(selectedItems.length))
                    .replace('{status}', options.find((o: any) => o.code === selectedStatus)?.[language === 'fr' ? 'titleFrench' : 'titleEnglish'] || selectedStatus)
                    .replace('{s}', selectedItems.length !== 1 ? 's' : '')
                : `This will update ${selectedItems.length} record${selectedItems.length !== 1 ? 's' : ''} to status "${options.find((o: any) => o.code === selectedStatus)?.[language === 'fr' ? 'titleFrench' : 'titleEnglish'] || selectedStatus}".`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel variant="elliptical-outline" disabled={isSubmitting}>{t('cancel') || 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm} disabled={isSubmitting} variant="elliptical">
              {isSubmitting ? (t('processing') || 'Processing...') : (t('yesUpdate') || 'Yes, update')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}; 